import { ProgramEngagementModel, ProgramModel, ProgramTaskRole, UserId } from '@cibo/core'
import { useFields } from '@cibo/landmanager'
import { useAuth } from '@cibo/profile'
import { useProgramEngagement, useProgramModel } from '@cibo/programs'
import { UseQueryResult } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { EngagementRouteParams } from '../../navigation/types/engagementRoutes'

type ProgramEngagementState = {
  basePath: string
  program: UseQueryResult<ProgramModel | null, unknown>
  onSuccess: () => void
  ownerName: string
  engagement: UseQueryResult<ProgramEngagementModel | null, unknown>
  userRole: ProgramTaskRole
  userId: UserId
}

const DEFAULT_VALUES = {
  basePath: '',
  ownerName: '',
  engagement: {} as UseQueryResult<ProgramEngagementModel | null, unknown>,
  userRole: 'participant',
  program: {},
} as ProgramEngagementState

export const ProgramEngagementContext = createContext<ProgramEngagementState>(DEFAULT_VALUES)

type ProgramEngagementProviderProps = {
  basePath: string
  children: React.ReactNode
  programId: string
  userId: UserId
}

export const ProgramEngagementProvider = ({
  basePath,
  programId,
  children,
  userId,
}: ProgramEngagementProviderProps) => {
  const { t } = useTranslation('@cibo/impact/ProgramEngagementProvider')
  const { engagementId } = useParams<EngagementRouteParams>()
  const engagement = useProgramEngagement({ id: engagementId })
  const program = useProgramModel({ programId: engagement.data?.programId ?? programId })
  const engagementFields = useFields(engagement.data?.fields ?? [])
  const { userId: selfId } = useAuth()
  const userRole = selfId === engagement.data?.ownedBy?.userId ? 'participant' : 'manager'
  const ownerName = engagementFields.data?.[0]?.ownerLabel ?? (t('applicant') as string)

  const onSuccess = () => engagement.refetch()

  return (
    <ProgramEngagementContext.Provider
      value={{
        basePath,
        program,
        onSuccess,
        ownerName,
        engagement,
        userRole,
        userId,
      }}
    >
      {children}
    </ProgramEngagementContext.Provider>
  )
}

export const useProgramEngagementContext = () => useContext(ProgramEngagementContext)
